import { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../utils/msalConfig";
import { RoleGroups } from "../roles/roleGroups"

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    if (inProgress !== "none") {
      return;
    }

    if (!accounts || accounts.length === 0) {
      setLoading(false);
      return;
    }

    const fetchGroups = async () => {
      try {
        setLoading(true);
        // Acquire access token silently
        const tokenResp = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        const accessToken = tokenResp.accessToken;

        // Call Graph API: check membership for specific groups
        const res = await fetch(
          "https://graph.microsoft.com/v1.0/me/checkMemberGroups",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              groupIds: [RoleGroups.Admin, RoleGroups.Interviewer],
            }),
          }
        );

        const graphData = await res?.json();

        const userGroupIds = graphData.value || [];
        setGroups(userGroupIds);

        // Determine if user is in allowed groups
        const allowed = userGroupIds.some((id) =>
          Object.values(RoleGroups).includes(id)
        );
        setIsAllowed(allowed);
      } catch (err) {
        console.error("Failed to fetch user groups:", err);
        setGroups([]);
        setIsAllowed(false);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [accounts, instance, inProgress]);

  const hasGroup = (groupId) => groups.includes(groupId);

  return (
    <AuthContext.Provider
      value={{ groups, hasGroup, loading, isAllowed, setGroups }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);